import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { useEffect, useMemo, useState } from 'react'
import { BtnGhost, BtnPrimary, Field, Select, TextArea, TextInput } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { centsFromDollars, dollarsFromCents, fmtDate } from '@/lib/labels'

type Tab = 'tarifs' | 'zones' | 'simulateur' | 'points' | 'colis' | 'tournees'

type Pickup = {
  id: string
  code: string
  name: string
  communeId: string
  commune: string
  type: string
  address: string
  hours: string
  active: boolean
}

type CommuneRow = {
  id: string
  name: string
  active: boolean
  homeDeliveryEnabled: boolean
  pickupEnabled: boolean
  codEnabled: boolean
  homeFeeCents: number | null
  freeAboveCents: number | null
  minOrderCents: number | null
  etaLabel: string | null
  unavailableMessage: string | null
  internalNote: string | null
  effectiveHomeFeeCents: number
  effectiveFreeAboveCents: number
  effectiveMinOrderCents: number
  activePickupPoints: number
  openOrders: number
}

type ParcelRow = {
  id: string
  status: string
  order: {
    reference: string
    contactName: string
    mode: string
    commune?: { name: string } | null
  }
  courier: { fullName: string } | null
  pickupPoint: { name: string; commune?: { name: string } | null } | null
}

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'tarifs', label: 'Tarifs' },
  { id: 'zones', label: 'Zones' },
  { id: 'simulateur', label: 'Simulateur' },
  { id: 'points', label: 'Points' },
  { id: 'colis', label: 'Colis' },
  { id: 'tournees', label: 'Tournées' },
]

function MoneyStepper({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  const cents = centsFromDollars(value)
  function bump(delta: number) {
    onChange(dollarsFromCents(Math.max(0, cents + delta)))
  }
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => bump(-100)}
          className="kicker flex h-9 w-9 items-center justify-center rounded-pilule border border-bordure-admin text-encre-admin"
          aria-label="Diminuer d’un dollar"
        >
          −
        </button>
        <span className="min-w-20 text-center font-titre text-lg tabular-nums text-encre-admin">
          {formatMoney(cents)}
        </span>
        <button
          type="button"
          onClick={() => bump(100)}
          className="kicker flex h-9 w-9 items-center justify-center rounded-pilule border border-bordure-admin text-encre-admin"
          aria-label="Augmenter d’un dollar"
        >
          +
        </button>
      </div>
    </Field>
  )
}

export function Livraisons() {
  const [tab, setTab] = useState<Tab>('tarifs')
  const toast = useToast()
  const qc = useQueryClient()
  const [communeFilter, setCommuneFilter] = useState<string>('all')

  const settings = useQuery({
    queryKey: ['admin-delivery-settings'],
    queryFn: () =>
      api<{
        homeFeeCents: number
        pickupFeeCents: number
        freeAboveCents: number
        homeEnabled: boolean
        pickupEnabled: boolean
        freeAppliesToPickup: boolean
        minOrderCents: number
        codMaxCents: number
        homeEtaLabel: string
        pickupEtaLabel: string
        outOfZoneMessage: string | null
      }>('/admin/delivery/settings'),
  })
  const [home, setHome] = useState('8.00')
  const [pickup, setPickup] = useState('0.00')
  const [freeAbove, setFreeAbove] = useState('120.00')
  const [homeEnabled, setHomeEnabled] = useState(true)
  const [pickupEnabled, setPickupEnabled] = useState(true)
  const [homeEta, setHomeEta] = useState('24–48 h')
  const [pickupEta, setPickupEta] = useState('Prêt sous 24 h')
  const [outOfZone, setOutOfZone] = useState('')

  useEffect(() => {
    if (settings.data) {
      setHome(dollarsFromCents(settings.data.homeFeeCents))
      setPickup(dollarsFromCents(settings.data.pickupFeeCents))
      setFreeAbove(dollarsFromCents(settings.data.freeAboveCents))
      setHomeEnabled(settings.data.homeEnabled)
      setPickupEnabled(settings.data.pickupEnabled)
      setHomeEta(settings.data.homeEtaLabel)
      setPickupEta(settings.data.pickupEtaLabel)
      setOutOfZone(settings.data.outOfZoneMessage ?? '')
    }
  }, [settings.data])

  const saveSettings = useMutation({
    mutationFn: () =>
      apiMutate('/admin/delivery/settings', 'PATCH', {
        homeFeeCents: centsFromDollars(home),
        pickupFeeCents: centsFromDollars(pickup),
        freeAboveCents: centsFromDollars(freeAbove),
        homeEnabled,
        pickupEnabled,
        homeEtaLabel: homeEta,
        pickupEtaLabel: pickupEta,
        outOfZoneMessage: outOfZone || null,
      }),
    onSuccess: () => {
      toast.ok('Tarifs enregistrés')
      void qc.invalidateQueries({ queryKey: ['admin-delivery-settings'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const zones = useQuery({
    queryKey: ['admin-communes'],
    queryFn: () => api<{ items: CommuneRow[] }>('/admin/delivery/communes'),
    enabled: tab === 'zones' || tab === 'points' || tab === 'simulateur',
  })

  const [zoneOpen, setZoneOpen] = useState(false)
  const [editZone, setEditZone] = useState<CommuneRow | null>(null)
  const [zf, setZf] = useState({
    active: true,
    homeDeliveryEnabled: true,
    pickupEnabled: true,
    codEnabled: true,
    homeFee: '',
    freeAbove: '',
    minOrder: '',
    etaLabel: '',
    unavailableMessage: '',
    internalNote: '',
  })

  function openEditZone(c: CommuneRow) {
    setEditZone(c)
    setZf({
      active: c.active,
      homeDeliveryEnabled: c.homeDeliveryEnabled,
      pickupEnabled: c.pickupEnabled,
      codEnabled: c.codEnabled,
      homeFee: c.homeFeeCents != null ? dollarsFromCents(c.homeFeeCents) : '',
      freeAbove: c.freeAboveCents != null ? dollarsFromCents(c.freeAboveCents) : '',
      minOrder: c.minOrderCents != null ? dollarsFromCents(c.minOrderCents) : '',
      etaLabel: c.etaLabel ?? '',
      unavailableMessage: c.unavailableMessage ?? '',
      internalNote: c.internalNote ?? '',
    })
    setZoneOpen(true)
  }

  const saveZone = useMutation({
    mutationFn: async () => {
      if (!editZone) return
      const body = {
        active: zf.active,
        homeDeliveryEnabled: zf.homeDeliveryEnabled,
        pickupEnabled: zf.pickupEnabled,
        codEnabled: zf.codEnabled,
        homeFeeCents: zf.homeFee === '' ? null : centsFromDollars(zf.homeFee),
        freeAboveCents: zf.freeAbove === '' ? null : centsFromDollars(zf.freeAbove),
        minOrderCents: zf.minOrder === '' ? null : centsFromDollars(zf.minOrder),
        etaLabel: zf.etaLabel || null,
        unavailableMessage: zf.unavailableMessage || null,
        internalNote: zf.internalNote || null,
        confirmImpact: true,
      }
      return apiMutate(`/admin/delivery/communes/${editZone.id}`, 'PATCH', body)
    },
    onSuccess: () => {
      toast.ok('Zone mise à jour')
      setZoneOpen(false)
      void qc.invalidateQueries({ queryKey: ['admin-communes'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const importKin = useMutation({
    mutationFn: () => apiMutate('/admin/delivery/communes/import-kinshasa', 'POST'),
    onSuccess: (r: unknown) => {
      const created = (r as { created?: number })?.created ?? 0
      toast.ok(`${created} commune(s) ajoutée(s)`)
      void qc.invalidateQueries({ queryKey: ['admin-communes'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const [simMode, setSimMode] = useState<'HOME' | 'PICKUP_POINT'>('HOME')
  const [simCommuneId, setSimCommuneId] = useState('')
  const [simSubtotal, setSimSubtotal] = useState('50.00')
  const [simResult, setSimResult] = useState<{
    allowed: boolean
    code: string | null
    message: string | null
    feeCents: number
    etaLabel: string | null
    codAllowed: boolean
  } | null>(null)

  const simulate = useMutation({
    mutationFn: () =>
      apiMutate('/admin/delivery/simulate', 'POST', {
        mode: simMode,
        communeId: simCommuneId || undefined,
        subtotalCents: centsFromDollars(simSubtotal),
        paymentMethod: 'CASH_ON_DELIVERY',
      }),
    onSuccess: (r) => setSimResult(r as typeof simResult),
    onError: (e: Error) => toast.err(e.message),
  })

  const points = useQuery({
    queryKey: ['admin-pickup-points'],
    queryFn: () => api<{ items: Pickup[] }>('/admin/delivery/pickup-points'),
    enabled: tab === 'points',
  })
  const communes = zones

  const [pointOpen, setPointOpen] = useState(false)
  const [editPoint, setEditPoint] = useState<Pickup | null>(null)
  const [pf, setPf] = useState({
    code: '',
    name: '',
    communeId: '',
    type: 'KIOSQUE',
    address: '',
    hours: '9h–18h',
  })

  const savePoint = useMutation({
    mutationFn: () => {
      if (editPoint) {
        return apiMutate(`/admin/delivery/pickup-points/${editPoint.id}`, 'PATCH', {
          name: pf.name,
          communeId: pf.communeId,
          type: pf.type,
          address: pf.address,
          hours: pf.hours,
        })
      }
      return apiMutate('/admin/delivery/pickup-points', 'POST', pf)
    },
    onSuccess: () => {
      toast.ok(editPoint ? 'Point mis à jour' : 'Point créé')
      setPointOpen(false)
      setEditPoint(null)
      void qc.invalidateQueries({ queryKey: ['admin-pickup-points'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const toggle = useMutation({
    mutationFn: (id: string) =>
      apiMutate(`/admin/delivery/pickup-points/${id}/toggle`, 'POST'),
    onSuccess: () => {
      toast.ok('Point basculé')
      void qc.invalidateQueries({ queryKey: ['admin-pickup-points'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const parcels = useQuery({
    queryKey: ['admin-parcels'],
    queryFn: () => api<{ items: ParcelRow[] }>('/admin/delivery/parcels'),
    enabled: tab === 'colis',
  })

  const rounds = useQuery({
    queryKey: ['admin-rounds'],
    queryFn: () =>
      api<{
        items: Array<{
          id: string
          date: string
          status: string
          zones: string[]
          courier: { fullName: string } | null
          parcelCount: number
        }>
      }>('/admin/delivery/rounds'),
    enabled: tab === 'tournees',
  })

  const allPoints = points.data?.items ?? []
  const filteredPoints = useMemo(() => {
    if (communeFilter === 'all') return allPoints
    return allPoints.filter((p) => p.communeId === communeFilter)
  }, [allPoints, communeFilter])

  const pointCounts = useMemo(() => {
    const source = filteredPoints
    return {
      active: source.filter((p) => p.active).length,
      inactive: source.filter((p) => !p.active).length,
    }
  }, [filteredPoints])

  function openCreatePoint() {
    setEditPoint(null)
    setPf({
      code: '',
      name: '',
      communeId: communes.data?.items[0]?.id ?? '',
      type: 'KIOSQUE',
      address: '',
      hours: '9h–18h',
    })
    setPointOpen(true)
  }

  function openEditPoint(p: Pickup) {
    setEditPoint(p)
    setPf({
      code: p.code,
      name: p.name,
      communeId: p.communeId,
      type: p.type,
      address: p.address,
      hours: p.hours,
    })
    setPointOpen(true)
  }

  function remiseLabel(p: ParcelRow) {
    if (p.order.mode === 'HOME') return 'Domicile'
    return `Relais · ${p.pickupPoint?.name ?? '—'}`
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`kicker rounded-pilule px-3 py-1.5 ${
              tab === t.id
                ? 'bg-encre-admin text-creme'
                : 'border border-bordure-admin text-encre-admin'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'tarifs' ? (
        <section className="carte-admin grid max-w-xl gap-4 p-5">
          <MoneyStepper
            label={`Domicile (${formatMoney(centsFromDollars(home))})`}
            value={home}
            onChange={setHome}
          />
          <MoneyStepper
            label={`Point relais (${formatMoney(centsFromDollars(pickup))})`}
            value={pickup}
            onChange={setPickup}
          />
          <MoneyStepper
            label={`Offerte dès (${formatMoney(centsFromDollars(freeAbove))})`}
            value={freeAbove}
            onChange={setFreeAbove}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={homeEnabled}
              onChange={(e) => setHomeEnabled(e.target.checked)}
            />
            Livraison à domicile activée
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={pickupEnabled}
              onChange={(e) => setPickupEnabled(e.target.checked)}
            />
            Points relais activés
          </label>
          <Field label="Délai domicile">
            <TextInput value={homeEta} onChange={(e) => setHomeEta(e.target.value)} />
          </Field>
          <Field label="Délai retrait">
            <TextInput value={pickupEta} onChange={(e) => setPickupEta(e.target.value)} />
          </Field>
          <Field label="Message hors zone">
            <TextArea rows={2} value={outOfZone} onChange={(e) => setOutOfZone(e.target.value)} />
          </Field>
          <p className="text-sm text-second-admin">
            Aperçu cliente : domicile {formatMoney(centsFromDollars(home))} · relais{' '}
            {formatMoney(centsFromDollars(pickup))} · offerte dès{' '}
            {formatMoney(centsFromDollars(freeAbove))}.
          </p>
          <BtnPrimary disabled={saveSettings.isPending} onClick={() => saveSettings.mutate()}>
            Enregistrer les tarifs
          </BtnPrimary>
        </section>
      ) : null}

      {tab === 'zones' ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <BtnGhost disabled={importKin.isPending} onClick={() => importKin.mutate()}>
              Importer Kinshasa (24)
            </BtnGhost>
            <p className="kicker text-second-admin">
              {zones.data?.items.filter((c) => c.active).length ?? 0} actives ·{' '}
              {zones.data?.items.length ?? 0} au total
            </p>
          </div>
          {zones.isLoading ? (
            <p className="text-second-admin">Chargement…</p>
          ) : !(zones.data?.items.length) ? (
            <EmptyState title="Aucune commune" />
          ) : (
            <section className="carte-admin overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-bordure-admin">
                    {['Commune', 'Desserte', 'Frais', 'COD', 'Points', ''].map((h) => (
                      <th key={h || 'a'} className="px-4 py-2.5">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {zones.data.items.map((c) => (
                    <tr key={c.id} className="border-b border-bordure-admin last:border-0">
                      <td className="px-4 py-2.5 font-medium">{c.name}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          <StatusPill
                            label={c.active ? 'Active' : 'Inactive'}
                            tone={c.active ? 'ok' : 'neutral'}
                          />
                          {c.homeDeliveryEnabled ? (
                            <StatusPill label="Domicile" tone="neutral" />
                          ) : null}
                          {c.pickupEnabled ? <StatusPill label="Relais" tone="neutral" /> : null}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">
                        {formatMoney(c.effectiveHomeFeeCents)}
                        {c.homeFeeCents == null ? (
                          <span className="text-second-admin"> · global</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusPill
                          label={c.codEnabled ? 'Oui' : 'Non'}
                          tone={c.codEnabled ? 'ok' : 'neutral'}
                        />
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">{c.activePickupPoints}</td>
                      <td className="px-4 py-2.5 text-right">
                        <RowActions
                          items={[{ label: 'Modifier', onSelect: () => openEditZone(c) }]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      ) : null}

      {tab === 'simulateur' ? (
        <section className="carte-admin grid max-w-xl gap-4 p-5">
          <Field label="Mode">
            <Select
              value={simMode}
              onChange={(e) => setSimMode(e.target.value as 'HOME' | 'PICKUP_POINT')}
            >
              <option value="HOME">Domicile</option>
              <option value="PICKUP_POINT">Point relais</option>
            </Select>
          </Field>
          <Field label="Commune">
            <Select value={simCommuneId} onChange={(e) => setSimCommuneId(e.target.value)}>
              <option value="">—</option>
              {(zones.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Sous-total (USD)">
            <TextInput value={simSubtotal} onChange={(e) => setSimSubtotal(e.target.value)} />
          </Field>
          <BtnPrimary disabled={simulate.isPending} onClick={() => simulate.mutate()}>
            Simuler
          </BtnPrimary>
          {simResult ? (
            <div className="rounded-md border border-bordure-admin p-3 text-sm">
              <p>
                {simResult.allowed ? 'Autorisé' : 'Refusé'}
                {simResult.code ? ` · ${simResult.code}` : ''}
              </p>
              {simResult.message ? <p className="text-second-admin">{simResult.message}</p> : null}
              <p className="mt-2">
                Frais {formatMoney(simResult.feeCents)}
                {simResult.etaLabel ? ` · ${simResult.etaLabel}` : ''}
                {simResult.codAllowed ? ' · COD ok' : ' · COD non'}
              </p>
            </div>
          ) : null}
        </section>
      ) : null}

      {tab === 'points' ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCommuneFilter('all')}
                className={`kicker rounded-pilule px-3 py-1.5 ${
                  communeFilter === 'all'
                    ? 'bg-encre-admin text-creme'
                    : 'border border-bordure-admin text-encre-admin'
                }`}
              >
                Tous
              </button>
              {(communes.data?.items ?? []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCommuneFilter(c.id)}
                  className={`kicker rounded-pilule px-3 py-1.5 ${
                    communeFilter === c.id
                      ? 'bg-encre-admin text-creme'
                      : 'border border-bordure-admin text-encre-admin'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            <p className="kicker text-second-admin">
              {pointCounts.active} actifs · {pointCounts.inactive} inactifs
            </p>
            <div className="ml-auto">
              <BtnPrimary onClick={openCreatePoint}>Nouveau point</BtnPrimary>
            </div>
          </div>
          {points.isLoading ? (
            <p className="text-second-admin">Chargement…</p>
          ) : filteredPoints.length === 0 ? (
            <EmptyState
              title="Aucun point relais"
              action={<BtnPrimary onClick={openCreatePoint}>Nouveau point</BtnPrimary>}
            />
          ) : (
            <section className="carte-admin overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-bordure-admin">
                    {['Code', 'Nom', 'Commune', 'Type', 'Actif', ''].map((h) => (
                      <th key={h || 'a'} className="px-4 py-2.5">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPoints.map((p) => (
                    <tr key={p.id} className="border-b border-bordure-admin last:border-0">
                      <td className="px-4 py-2.5 font-medium">{p.code}</td>
                      <td className="px-4 py-2.5">{p.name}</td>
                      <td className="px-4 py-2.5">{p.commune}</td>
                      <td className="px-4 py-2.5">{p.type}</td>
                      <td className="px-4 py-2.5">
                        <StatusPill
                          label={p.active ? 'Actif' : 'Inactif'}
                          tone={p.active ? 'ok' : 'neutral'}
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <RowActions
                          items={[
                            { label: 'Modifier', onSelect: () => openEditPoint(p) },
                            {
                              label: 'Basculer',
                              onSelect: () => toggle.mutate(p.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      ) : null}

      {tab === 'colis' ? (
        parcels.isLoading ? (
          <p className="text-second-admin">Chargement…</p>
        ) : !(parcels.data?.items.length) ? (
          <EmptyState title="Aucun colis aujourd’hui" />
        ) : (
          <section className="carte-admin overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-bordure-admin">
                  {['Commande', 'Client', 'Mode', 'Remise', 'Statut', 'Coursier / Point', ''].map(
                    (h) => (
                      <th key={h || 'a'} className="px-4 py-2.5">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {parcels.data.items.map((p) => (
                  <tr key={p.id} className="border-b border-bordure-admin last:border-0">
                    <td className="px-4 py-2.5">
                      <a href={`/commandes/${p.order.reference}`} className="font-medium">
                        {p.order.reference}
                      </a>
                    </td>
                    <td className="px-4 py-2.5">{p.order.contactName}</td>
                    <td className="px-4 py-2.5">{p.order.mode}</td>
                    <td className="px-4 py-2.5">{remiseLabel(p)}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill label={p.status} tone="neutral" />
                    </td>
                    <td className="px-4 py-2.5 text-second-admin">
                      {p.courier?.fullName ?? p.pickupPoint?.name ?? '—'}
                      {p.pickupPoint?.commune?.name || p.order.commune?.name
                        ? ` · ${p.pickupPoint?.commune?.name ?? p.order.commune?.name}`
                        : null}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <RowActions
                        items={[
                          {
                            label: 'Ouvrir commande',
                            onSelect: () => {
                              window.location.href = `/commandes/${p.order.reference}`
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      ) : null}

      {tab === 'tournees' ? (
        rounds.isLoading ? (
          <p className="text-second-admin">Chargement…</p>
        ) : !(rounds.data?.items.length) ? (
          <EmptyState title="Aucune tournée" />
        ) : (
          <section className="carte-admin overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-bordure-admin">
                  {['Date', 'Coursier', 'Zones', 'Colis', 'Statut'].map((h) => (
                    <th key={h} className="px-4 py-2.5">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rounds.data.items.map((r) => (
                  <tr key={r.id} className="border-b border-bordure-admin last:border-0">
                    <td className="px-4 py-2.5">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5">{r.courier?.fullName ?? '—'}</td>
                    <td className="px-4 py-2.5 text-second-admin">
                      {(r.zones ?? []).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{r.parcelCount}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill label={r.status} tone="neutral" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      ) : null}

      <Modal
        open={zoneOpen}
        title={editZone ? `Zone · ${editZone.name}` : 'Zone'}
        onClose={() => setZoneOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setZoneOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary disabled={saveZone.isPending} onClick={() => saveZone.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {(
            [
              ['active', 'Zone active'],
              ['homeDeliveryEnabled', 'Domicile'],
              ['pickupEnabled', 'Point relais'],
              ['codEnabled', 'Paiement à la livraison'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={zf[key]}
                onChange={(e) => setZf((f) => ({ ...f, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
          <Field label="Frais domicile (vide = global)">
            <TextInput
              value={zf.homeFee}
              onChange={(e) => setZf((f) => ({ ...f, homeFee: e.target.value }))}
              placeholder="ex. 8.00"
            />
          </Field>
          <Field label="Offerte dès (vide = global)">
            <TextInput
              value={zf.freeAbove}
              onChange={(e) => setZf((f) => ({ ...f, freeAbove: e.target.value }))}
            />
          </Field>
          <Field label="Minimum commande (vide = global)">
            <TextInput
              value={zf.minOrder}
              onChange={(e) => setZf((f) => ({ ...f, minOrder: e.target.value }))}
            />
          </Field>
          <Field label="Délai">
            <TextInput
              value={zf.etaLabel}
              onChange={(e) => setZf((f) => ({ ...f, etaLabel: e.target.value }))}
            />
          </Field>
          <Field label="Message indisponible">
            <TextArea
              rows={2}
              value={zf.unavailableMessage}
              onChange={(e) => setZf((f) => ({ ...f, unavailableMessage: e.target.value }))}
            />
          </Field>
          <Field label="Note interne">
            <TextArea
              rows={2}
              value={zf.internalNote}
              onChange={(e) => setZf((f) => ({ ...f, internalNote: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>

      <Modal
        open={pointOpen}
        title={editPoint ? 'Modifier le point' : 'Nouveau point relais'}
        onClose={() => setPointOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setPointOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary disabled={savePoint.isPending} onClick={() => savePoint.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {!editPoint ? (
            <Field label="Code">
              <TextInput
                value={pf.code}
                onChange={(e) => setPf((f) => ({ ...f, code: e.target.value }))}
              />
            </Field>
          ) : null}
          <Field label="Nom">
            <TextInput
              value={pf.name}
              onChange={(e) => setPf((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
          <Field label="Commune">
            <Select
              value={pf.communeId}
              onChange={(e) => setPf((f) => ({ ...f, communeId: e.target.value }))}
            >
              {(communes.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Type">
            <Select
              value={pf.type}
              onChange={(e) => setPf((f) => ({ ...f, type: e.target.value }))}
            >
              {['KIOSQUE', 'PHARMACIE', 'COMMERCE', 'STATION', 'DEPOT'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Adresse">
            <TextInput
              value={pf.address}
              onChange={(e) => setPf((f) => ({ ...f, address: e.target.value }))}
            />
          </Field>
          <Field label="Horaires">
            <TextInput
              value={pf.hours}
              onChange={(e) => setPf((f) => ({ ...f, hours: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>
    </div>
  )
}
